import React, {
  useState,
  useEffect,
  lazy,
  Suspense,
  useCallback,
  memo,
  useMemo,
} from "react";
import VendorDropdown from "./VendorDropdown";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import { selectUser } from "../../../features/userSlice";

const DataGridPro = lazy(() =>
  import("@mui/x-data-grid-pro/DataGridPro").then((module) => ({
    default: module.DataGridPro,
  })),
);

const updateRowPosition = (initialIndex, newIndex, rows) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const rowsClone = [...rows];
      const row = rowsClone.splice(initialIndex, 1)[0];
      rowsClone.splice(newIndex, 0, row);
      resolve(rowsClone);
    }, 100);
  });
};

// Memoize the component to prevent unnecessary re-renders
const VendorRankings = memo(({ preferences, onPreferencesUpdate }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const user = useSelector(selectUser);

  // Use useCallback for the row order change handler
  const handleRowOrderChange = useCallback(
    async (params) => {
      setLoading(true);
      const newRows = await updateRowPosition(
        params.oldIndex,
        params.targetIndex,
        rows,
      );

      const updatedRows = newRows.map((row, index) => {
        const newRank = index + 1;
        // Update preferences with rank as number
        onPreferencesUpdate(row.id, { rank: newRank });
        return {
          ...row,
          rank: newRank,
        };
      });

      setRows(updatedRows);
      setLoading(false);
    },
    [rows, onPreferencesUpdate],
  );

  // Only update rows when preferences change
  useEffect(() => {
    if (!preferences || Object.keys(preferences).length === 0) {
      return;
    }

    const initialRows = Object.entries(preferences).map(
      ([vendorCode, vendorPrefs]) => ({
        id: vendorCode,
        vendor: vendorCode,
        vendorName: vendorCode.toUpperCase(),
        rank: Number(vendorPrefs.rank) || 15,
        freeShippingMinimum: vendorPrefs.free_shipping_minimum ?? 200,
        warehouses: vendorPrefs.warehouses || [],
        disabled: vendorPrefs.disabled ?? false,
      }),
    );

    initialRows.sort((a, b) => a.rank - b.rank);

    const updatedRows = initialRows.map((row, index) => ({
      ...row,
      rank: index + 1,
    }));

    setRows(updatedRows);
  }, [preferences]);

  // Memoize renderCell functions
  const renderRankCell = useCallback(
    (params) => (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "flex-start",
          height: "100%",
          justifyContent: "center",
          paddingTop: "10px",
          fontSize: "18px",
          fontWeight: 500,
        }}
      >
        {params.value}
      </Box>
    ),
    [],
  );

  const renderVendorCell = useCallback(
    (params) => (
      <VendorDropdown
        key={params.row.id}
        row={{
          ...params.row,
          vendor: params.row.vendor,
          logoUrl: `https://d34c0c1in94n75.cloudfront.net/${params.row.vendor}_logo.png`,
        }}
        preferences={preferences}
        onPreferencesUpdate={onPreferencesUpdate}
      />
    ),
    [preferences, onPreferencesUpdate],
  );

  const columns = useMemo(
    () => [
      {
        field: "rank",
        headerName: "Rank",
        width: 70,
        sortable: false,
        renderCell: renderRankCell,
      },
      {
        field: "vendor",
        headerName: "Supplier",
        flex: 1,
        sortable: false,
        renderCell: renderVendorCell,
      },
    ],
    [renderRankCell, renderVendorCell],
  );

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Suspense fallback={<div>Loading grid...</div>}>
        {rows.length > 0 && (
          <DataGridPro
            rows={rows}
            columns={columns}
            loading={loading}
            rowReordering
            onRowOrderChange={handleRowOrderChange}
            disableColumnMenu
            hideFooter
            disableSelectionOnClick
            getRowHeight={() => "auto"}
            sx={{
              height: "100%",
              "& .MuiDataGrid-main": {
                overflow: "hidden",
              },
              "& .MuiDataGrid-cell": {
                padding: 0,
                border: "none",
              },
              "& .MuiDataGrid-columnHeaders": {
                borderBottom: "1px solid rgba(224, 224, 224, 1)",
                "& .MuiDataGrid-columnHeader": {
                  padding: "0 16px",
                  height: "48px",
                  "&:focus, &:focus-within": {
                    outline: "none",
                  },
                },
              },
              "& .MuiDataGrid-virtualScroller": {
                marginTop: "0!important",
                overflowY: "auto",
                marginBottom: "0!important",
                paddingBottom: "0!important",
                "&::-webkit-scrollbar-track": {
                  marginBottom: 0,
                },
              },
              "& .MuiDataGrid-row": {
                position: "relative",
                "& .MuiDataGrid-cell": {
                  position: "relative",
                  zIndex: 1,
                },
                "&:first-of-type": {
                  borderTop: "none",
                },
                "&:last-of-type": {
                  borderBottom: "none",
                },
                "&:hover": {
                  backgroundColor: "#F5F5F5",
                },
                "&:has(.expanded-row)": {
                  backgroundColor: "transparent !important",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "48px",
                    backgroundColor: "#F5F5F5",
                    borderBottom: "1px dashed rgba(0, 0, 0, 0.12)",
                    pointerEvents: "none",
                    zIndex: 0,
                  },
                },
                "& .MuiDataGrid-cell:focus-within": {
                  outline: "none",
                },
                "&.Mui-expanded .MuiDataGrid-rowReorderCell": {
                  alignItems: "flex-start",
                  paddingTop: "13px",
                },
              },
              "& .MuiDataGrid-row.Mui-selected": {
                backgroundColor: "transparent",
              },
              "& .MuiDataGrid-row.Mui-selected:hover": {
                backgroundColor: "#F5F5F5",
              },
              "& .MuiDataGrid-cell:focus": {
                outline: "none",
              },
              "& .MuiDataGrid-cell:focus-within": {
                outline: "none",
              },
              "& .MuiDataGrid-rowReorderCell": {
                alignItems: "flex-start",
                paddingTop: "12px",
              },
              "& .MuiDataGrid-footerContainer": {
                display: "none",
              },
              borderBottom: "1px solid rgba(224, 224, 224, 1)",
              "& .MuiDataGrid-root": {
                border: "none",
              },
            }}
          />
        )}
      </Suspense>
    </div>
  );
});

export default VendorRankings;
